import { define, auto } from 'type-r'
import { store } from '../models/store.js'
import ChartView from './chart_view.js'
import { unfactorNYTimeZone } from '../tz.js'
import { ensureGoogleCharts } from '../google_loader.js'

@define
export default class LineChartComponent extends ChartView {
    @auto(null) declare linedata: any;
    @auto(null) declare linechart: any;
    @auto(0) declare lineDt: number

    private _rowMap: Map<number, number> = new Map();
    private _chartsReady: Promise<void>;

    initialize() {
        super.initialize();
        this._chartsReady = ensureGoogleCharts();
        this.listenTo(this, "change:dd", this.onDataUpdated);
    }

    protected get choiceValue(): number { return 1; }

    protected onResized(): void {
        if (this.linedata) this.renderChart();
    }

    protected onShow(): void {
        if (this.linedata) this.renderChart();
    }

    protected onCpnChanging(): void {
        this.linedata = null;
        this._rowMap.clear();
    }

    protected parseExtra(data?: any) {
        if (data?.selCpn)
            delete data.selCpn;
        if (data?.dd?.length) {
            this.set({ dd: [] }, { silent: true });
        }
        return data;
    }

    protected fetchParams(): Record<string, any> {
        return { ...super.fetchParams(), type: 'checkline', tenyr: 1, nasdaq: 1, gnma: 1, fnma: 1 };
    }

    async onDataUpdated(): Promise<void> {
        if (!this.dd?.length) return;
        await this._chartsReady;

        let dataChanged = false;
        const targetColumnCount = store.rr != null ? 6 : 2;

        if (this.linedata == null || this.linedata.getNumberOfColumns() !== targetColumnCount) {
            this.linedata = new window.google.visualization.DataTable();
            this.linedata.addColumn('datetime', 'Time');
            this.linedata.addColumn('number', 'BPS');

            if (targetColumnCount === 6) {
                this.linedata.addColumn('number', 'Support');
                this.linedata.addColumn({ type: 'boolean', role: 'certainty' });
                this.linedata.addColumn('number', 'Resistance');
                this.linedata.addColumn({ type: 'boolean', role: 'certainty' });
                if (store.rr) {
                    store.rr.support = parseInt(store.rr.support);
                    store.rr.resistance = parseInt(store.rr.resistance);
                }
            }
            this._rowMap.clear();
            dataChanged = true;
        }

        const isExtended = targetColumnCount === 6;

        if (this.linedata.getNumberOfRows() > 0) {
            const lastRowIdx = this.linedata.getNumberOfRows() - 1;
            if (this.linedata.getValue(lastRowIdx, 1) === null) {
                this.linedata.removeRow(lastRowIdx);
                dataChanged = true;
            }
        }

        if (isExtended && store.rr != null) {
            const support = parseInt(store.rr.support);
            const resistance = parseInt(store.rr.resistance);
            for (let y = 0; y < this.linedata.getNumberOfRows(); y++) {
                if (this.linedata.getValue(y, 2) !== support || this.linedata.getValue(y, 4) !== resistance) {
                    this.linedata.setValue(y, 2, support);
                    this.linedata.setValue(y, 4, resistance);
                    dataChanged = true;
                }
            }
        }

        for (const point of this.dd) {
            const rawDt = point[0];
            const val = point[1];
            const dt = new Date(unfactorNYTimeZone(rawDt));
            const ts = dt.getTime();

            if (this._rowMap.has(ts)) {
                const rowIdx = this._rowMap.get(ts)!;
                if (this.linedata.getValue(rowIdx, 1) !== val) {
                    this.linedata.setValue(rowIdx, 1, val);
                    dataChanged = true;
                }
            } else {
                if (this.linedata.getNumberOfRows() > 0) {
                    const firstdt = this.linedata.getValue(0, 0);
                    if (firstdt.getDate() != dt.getDate()) {
                        window.location.reload();
                        return;
                    }
                }

                let rowIdx: number;
                if (isExtended && store.rr != null) {
                    rowIdx = this.linedata.addRow([dt, val, parseInt(store.rr.support), false, parseInt(store.rr.resistance), false]);
                } else {
                    rowIdx = this.linedata.addRow([dt, val]);
                }
                this._rowMap.set(ts, rowIdx);
                dataChanged = true;
            }
        }

        if (isExtended && this.linedata.getNumberOfRows() > 0 && store.rr != null) {
            const lastRowIdx = this.linedata.getNumberOfRows() - 1;
            const lastDt = this.linedata.getValue(lastRowIdx, 0);
            const dummyDt = new Date(lastDt.getFullYear(), lastDt.getMonth(), lastDt.getDate(), 17, 0, 0, 0);
            this.linedata.addRow([dummyDt, null, parseInt(store.rr.support), false, parseInt(store.rr.resistance), false]);
            dataChanged = true;
        }

        if (dataChanged || this.linechart == null) {
            this.renderChart();
        }
    }

    renderChart(): void {
        if (!this.linedata) return;

        const el = $(this.el);
        const chartWidth = el.width()!;
        const chartHeight = el.height()!;

        const series = [{ color: 'blue' }];
        if (this.linedata.getNumberOfColumns() === 6) {
            series.push({ color: 'green' }, { color: 'red' });
        }

        const options = {
            title: store.caption,
            width: chartWidth,
            height: chartHeight,
            yAxis: { title: '(BPS Change from Open)' },
            backgroundColor: ($ as any).template.ie7 || ($ as any).template.ie8 ? '#FFFFFF' : 'transparent',
            legendTextStyle: { color: 'black' },
            series: series,
            titleTextStyle: { color: 'black' },
            hAxis: {
                textStyle: { color: 'black' },
                viewWindow: {
                    max: new Date(unfactorNYTimeZone(this.maxdt)),
                    min: new Date(unfactorNYTimeZone(this.mindt))
                }
            },
            vAxis: {
                textStyle: { color: 'black' },
                baselineColor: '#666666'
            },
            chartArea: { top: 35, left: 50, width: chartWidth - 60, height: chartHeight - 75 },
            legend: 'bottom'
        };

        el.css('overflow', 'hidden');

        if (this.linechart == null) {
            this.linechart = new window.google.visualization.LineChart(this.el);
        }

        this.linechart.draw(this.linedata, options);
    }
}
